import React from 'react';
import { 
  Box, 
  Container, 
  Heading, 
  Text, 
  Badge,
  Icon,
  Flex,
  chakra
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { FaCogs, FaLeaf, FaWifi, FaShieldAlt } from 'react-icons/fa';
import { COLORS } from '../../globalvariables';
import { useLanguage } from '../../context/LanguageContext';
import { texts } from '../../lang';

const MotionHeading = chakra(motion.h2);
const MotionBox = chakra(motion.div);
const MotionText = chakra(motion.p);
const MotionFlex = chakra(motion.div);
const MotionIcon = chakra(motion.div);
const MotionImage = chakra(motion.img);

const MachineHeader = ({ machineTexts }) => {
  const { language } = useLanguage();
  const machineImages = texts[language].images.machines;

  return (
    <Box py={{ base: 8, md: 12 }} position="relative" overflow="hidden">
      {/* Background gradient */}
      <Box 
        position="absolute" 
        top={0} 
        left={0} 
        right={0} 
        bottom={0} 
        bgGradient={`linear(to-b, rgba(225, 219, 219, 0.4), ${COLORS.background})`}
        zIndex={0}
      />
      
      {/* Animated glow effect */}
      <Box
        position="absolute"
        top="50%"
        left="50%"
        transform="translate(-50%, -50%)"
        width={{ base: "300px", md: "500px" }}
        height={{ base: "300px", md: "500px" }}
        borderRadius="full"
        bg={`radial-gradient(circle, ${COLORS.primary}33 0%, transparent 70%)`}
        filter="blur(60px)"
        opacity={0.6}
      />
      
      <Container maxW="1200px" position="relative" zIndex={2} px={{ base: 2, md: 4 }}>
        <Flex 
          direction={{ base: "column", md: "row" }} 
          gap={{ base: 6, md: 8 }} 
          alignItems="center"
        >
          <Box flex="1">
            <MotionBox
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              mb={{ base: 6, md: 8 }}
            >
              <Badge 
                fontSize={{ base: "xs", md: "sm" }}
                colorScheme="green" 
                p={{ base: 1.5, md: 2 }}
                borderRadius="full" 
                mb={{ base: 3, md: 4 }}
              >
                {machineTexts.subtitle}
              </Badge>
              
              <MotionHeading
                as="h1"
                fontSize={{ base: "3xl", md: "5xl", lg: "6xl" }}
                fontWeight="bold"
                bgGradient={`linear(to-r, ${COLORS.textPrimary}, ${COLORS.primary} 50%, ${COLORS.textPrimary})`}
                bgClip="text"
                letterSpacing="tight"
                mb={{ base: 4, md: 6 }}
              >
                {machineTexts.title}
              </MotionHeading>
              
              <MotionText
                fontSize={{ base: "md", md: "xl" }}
                maxW="800px"
                color={COLORS.textPrimary}
              >
                {machineTexts.description}
              </MotionText>
              
              {/* Feature icons */}
              <MotionFlex
                justify="flex-start"
                wrap="wrap"
                gap={{ base: 2, md: 4 }}
                mt={{ base: 4, md: 6 }}
                initial="hidden"
                animate="visible"
                variants={{
                  visible: {
                    transition: {
                      staggerChildren: 0.1
                    }
                  }
                }}
              >
                {[
                  { icon: FaCogs, text: machineTexts.headerAutomation },
                  { icon: FaShieldAlt, text: machineTexts.headerQualityControl },
                  { icon: FaLeaf, text: machineTexts.headerEcoFriendly },
                  { icon: FaWifi, text: machineTexts.headerConnected }
                ].map((item, index) => (
                  <MotionFlex
                    key={index}
                    align="center" 
                    bg="transparent"
                    py={{ base: 1.5, md: 2 }}
                    px={{ base: 2, md: 4 }}
                    borderRadius="md"
                    borderWidth="1px"
                    borderColor={COLORS.primary}
                    variants={{
                      hidden: { opacity: 0, y: 20 },
                      visible: { 
                        opacity: 1, 
                        y: 0,
                        transition: {
                          type: "spring",
                          damping: 12
                        }
                      }
                    }}
                    _hover={{
                      bg: `${COLORS.primary}20`,
                      transform: "translateY(-2px)",
                      boxShadow: `0 4px 8px ${COLORS.primary}33`
                    }}
                    transition="all 0.3s ease"
                  >
                    <MotionIcon
                      as={item.icon}
                      color={COLORS.primary}
                      mr={{ base: 1.5, md: 2 }}
                      boxSize={{ base: "14px", md: "18px" }}
                      whileHover={{ rotate: 15 }}
                      transition={{ type: "spring", stiffness: 300 }}
                    />
                    <Text fontSize={{ base: "xs", md: "sm" }} fontWeight="medium">{item.text}</Text>
                  </MotionFlex>
                ))}
              </MotionFlex>
            </MotionBox>
          </Box>
          
          <Box flex="1">
            <MotionBox
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              display="flex"
              justifyContent="center"
            >
              <MotionImage
                src={machineImages.elite.display}
                alt="1000Shake Machine"
                objectFit="contain"
                borderRadius="lg"
                whileHover={{ 
                  scale: 1.03,
                  boxShadow: "0 0 40px rgba(110, 146, 119, 0.25)" 
                }}
                transition={{ duration: 0.3 }}
                mt={{ base: "20", md: "40" }}
                maxW={{ base: "100%", md: "auto" }}
                maxH={{ base: "300px", md: "600px" }}
              />
            </MotionBox>
          </Box>
        </Flex>
      </Container>
    </Box>
  );
};

export default MachineHeader;